import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const rawUrl = (import.meta.env.VITE_PUBLIC_SUPABASE_URL as string) ?? "";
const url = rawUrl.replace(/\/+$/, "");
const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

/**
 * Custom lock implementation with a timeout fallback.
 *
 * The default Supabase auth-js lock uses the Navigator LockManager API with
 * **infinite** acquire timeout (`-1`). When two browser tabs (or a tab and an
 * iframe/DevTools instance) race for the same lock, one of them can block
 * forever — which causes `getSession()` and every subsequent `.from().select()`
 * call to hang indefinitely, leaving the page stuck on "Loading…".
 *
 * This wrapper imposes a 10-second ceiling: if the Navigator lock can't be
 * acquired within that window the callback runs anyway, unblocking the app.
 */
const LOCK_TIMEOUT_MS = 10_000;

const supabaseLock = async <R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => {
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    const effectiveTimeout =
      acquireTimeout < 0 ? LOCK_TIMEOUT_MS : Math.min(acquireTimeout, LOCK_TIMEOUT_MS);

    try {
      return await new Promise<R>((resolve, reject) => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), effectiveTimeout);

        navigator.locks
          .request(
            name,
            { mode: "exclusive", signal: controller.signal },
            async (lock) => {
              clearTimeout(timer);
              return await fn();
            },
          )
          .then(resolve, reject);
      });
    } catch {
      return await fn();
    }
  }

  return await fn();
};

// Singleton — never create another client anywhere in the app
let _client: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient {
  if (!_client) {
    _client = createClient(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        lock: supabaseLock,
      },
    });
  }
  return _client;
}

export const supabase = getSupabase();

export type Profile = {
  id: string;
  full_name: string | null;
  role: string;
  job_title: string | null;
  avatar_color: string | null;
  initials: string | null;
  created_at: string;
  updated_at: string;
};